<template>
  <!-- Everything not declared below (label, error-messages, hide-no-data,
       density, variant, placeholder, …) falls through to the combobox as it
       is; Vue 3 has no need to name each one. -->
  <v-combobox
    v-model="input"
    v-model:search="searchInput"
    :loading="searchLoading"
    :items="searchResults"
    :item-title="itemText"
    :item-value="itemValue"
    :readonly="readonly"
    :clearable="!readonly"
    no-filter
  >
    <template #prepend-inner>
      <v-icon @click="select" color="primary">{{icon}}</v-icon>
    </template>
    <!-- A caller's mark inside the box, e.g. the work order's "objekti järgi". -->
    <template v-if="$slots['append-inner']" #append-inner>
      <slot name="append-inner" />
    </template>
    <!-- A caller's own suggestion row, e.g. the object form's "objekt olemas". -->
    <template v-if="$slots.item" #item="slotProps">
      <slot name="item" v-bind="slotProps" />
    </template>
  </v-combobox>
</template>

<script>
import debounce from 'lodash/debounce'
import api from '@/api'

export default {
  data () {
    return {
      input: undefined,
      searchInput: '',
      searchResults: [],
      searchLoading: false,
    }
  },
  emits: ['update:modelValue', 'view'],
  props: [
    'modelValue',
    'item-text',
    'item-value',
    'timeout',
    'searchMethod',
    'fallbackSearchMethod',
    'itemKey',
    'readonly'
  ],
  watch: {
    input (value) {
      if (value !== this.modelValue) {
        this.$emit('update:modelValue', value)
      }
    },
    modelValue (value) {
      this.input = value
    },
    searchInput (query) {
      if (!query || (this.modelValue && this.modelValue.id)) {
        return
      }
      this.runSearch(query)
    },
  },
  computed: {
    icon () {
      switch (this.itemKey) {
        case 'clients':
          return 'mdi-account-tie'
        case 'objects':
          return 'mdi-warehouse'
      }
      return undefined
    }
  },
  methods: {
    select () {
      if (this.input && this.input.id) {
        this.$emit('view', this.input)
      }
    }
  },
  mounted () {
    this.input = this.modelValue
  },
  created () {
    this.runSearch = debounce(async query => {
      // Numbered: a slow answer to "Sal" must not replace the answer to "Salme".
      const request = this.searchRequest = (this.searchRequest || 0) + 1
      const current = () => request === this.searchRequest
      this.searchLoading = true
      try {
        const fetchResults = method => api[method]({ query })
          .then(res => res[this.itemKey] || [])
          .catch(() => [])
        if (!this.fallbackSearchMethod) {
          const results = await fetchResults(this.searchMethod)
          if (current()) this.searchResults = results
          return
        }
        // Always also query the fallback source (e.g. the address gazetteer) and
        // show its hits after the primary ones, so existing matches come first
        // but real-world addresses the primary can't know about still surface.
        const [primaryResults, fallbackResults] = await Promise.all([
          fetchResults(this.searchMethod),
          fetchResults(this.fallbackSearchMethod)
        ])
        // De-dupe on addressKey when present (a normalized street + number, so an
        // ADS suggestion is dropped when an object already exists for the same
        // place despite differing display formats); fall back to the display text.
        const dedupKey = r => (r && (r.addressKey || r[this.itemText]))
        const seen = new Set(primaryResults.map(dedupKey))
        const extra = fallbackResults.filter(r => !seen.has(dedupKey(r)))
        if (current()) this.searchResults = [...primaryResults, ...extra]
      } catch (e) {
        // Ignore
      } finally {
        if (current()) this.searchLoading = false
      }
    }, this.timeout || 500)
  }
}
</script>